import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  Button,
  TextField,
  Grid,
  Alert,
  CircularProgress,
  Divider,
  IconButton,
} from '@mui/material';
import {
  CheckCircle as ApproveIcon,
  Cancel as RejectIcon,
  Close as CloseIcon,
  Person as PersonIcon,
  Home as AddressIcon,
  Description as DocumentIcon,
  Visibility as ViewIcon,
} from '@mui/icons-material';
import { KYCSubmission, KYCReviewRequest } from '../../types/kyc';
import { kycService } from '../../services/kycService';
import { uploadService } from '../../services/uploadService';
import KYCStatusBadge from './KYCStatusBadge';
import { useThemeMode } from '../../contexts/ThemeContext';

interface KYCReviewDialogProps {
  open: boolean;
  onClose: () => void;
  submission: KYCSubmission | null;
  onReviewComplete: () => void;
}

const KYCReviewDialog: React.FC<KYCReviewDialogProps> = ({
  open,
  onClose,
  submission,
  onReviewComplete,
}) => {
  const { mode } = useThemeMode();
  const isDark = mode === 'dark';

  const [action, setAction] = useState<'approve' | 'reject' | null>(null);
  const [reason, setReason] = useState('');
  const [notes, setNotes] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleClose = () => {
    setAction(null);
    setReason('');
    setNotes('');
    setError(null);
    onClose();
  };

  const handleSubmitReview = async () => {
    if (!submission || !action) return;

    if (action === 'reject' && !reason.trim()) {
      setError('Please provide a reason for rejection');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const reviewData: KYCReviewRequest = {
        action,
        notes: notes.trim() || undefined,
      };

      if (action === 'reject') {
        reviewData.reason = reason.trim();
      }

      await kycService.reviewSubmission(submission.user_id, reviewData);
      onReviewComplete();
      handleClose();
    } catch (err: any) {
      setError(err.response?.data?.detail || err.message || 'Failed to submit review');
    } finally {
      setLoading(false);
    }
  };

  const handleViewDocument = (fileUrl: string) => {
    const url = uploadService.getFileUrl(fileUrl);
    window.open(url, '_blank');
  };

  if (!submission) return null;

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          background: isDark ? 'rgba(17, 24, 39, 0.95)' : 'rgba(255, 255, 255, 0.98)',
          backdropFilter: 'blur(20px)',
          border: `1px solid ${isDark ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.08)'}`,
        },
      }}
    >
      <DialogTitle>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Typography
              variant="h5"
              sx={{
                fontWeight: 700,
                background: 'linear-gradient(135deg, #3B82F6 0%, #0EA5E9 100%)',
                backgroundClip: 'text',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
              }}
            >
              KYC Review
            </Typography>
            <KYCStatusBadge status={submission.status} />
          </Box>
          <IconButton onClick={handleClose} disabled={loading}>
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent dividers>
        {error && (
          <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {/* Personal Information */}
        <Box sx={{ mb: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
            <PersonIcon color="primary" />
            <Typography variant="h6" fontWeight={600}>
              Personal Information
            </Typography>
          </Box>
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <Typography variant="body2" color="text.secondary">Full Name</Typography>
              <Typography variant="body1" fontWeight={500}>{submission.full_name}</Typography>
            </Grid>
            <Grid item xs={12} md={6}>
              <Typography variant="body2" color="text.secondary">Date of Birth</Typography>
              <Typography variant="body1" fontWeight={500}>
                {new Date(submission.date_of_birth).toLocaleDateString()}
              </Typography>
            </Grid>
            <Grid item xs={12} md={6}>
              <Typography variant="body2" color="text.secondary">Nationality</Typography>
              <Typography variant="body1" fontWeight={500}>{submission.nationality}</Typography>
            </Grid>
            <Grid item xs={12} md={6}>
              <Typography variant="body2" color="text.secondary">Wallet Address</Typography>
              <Typography variant="body1" fontWeight={500} sx={{ fontFamily: 'monospace' }}>
                {submission.wallet_address
                  ? `${submission.wallet_address.slice(0, 10)}...${submission.wallet_address.slice(-8)}`
                  : 'N/A'}
              </Typography>
            </Grid>
          </Grid>
        </Box>

        <Divider sx={{ my: 3 }} />

        {/* Address */}
        <Box sx={{ mb: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
            <AddressIcon color="primary" />
            <Typography variant="h6" fontWeight={600}>
              Address
            </Typography>
          </Box>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Typography variant="body2" color="text.secondary">Street Address</Typography>
              <Typography variant="body1" fontWeight={500}>
                {submission.address?.address_line1}
                {submission.address?.address_line2 && <>, {submission.address.address_line2}</>}
              </Typography>
            </Grid>
            <Grid item xs={12} md={4}>
              <Typography variant="body2" color="text.secondary">City</Typography>
              <Typography variant="body1" fontWeight={500}>{submission.address?.city}</Typography>
            </Grid>
            <Grid item xs={12} md={4}>
              <Typography variant="body2" color="text.secondary">State/Province</Typography>
              <Typography variant="body1" fontWeight={500}>{submission.address?.state_province}</Typography>
            </Grid>
            <Grid item xs={12} md={4}>
              <Typography variant="body2" color="text.secondary">Postal Code</Typography>
              <Typography variant="body1" fontWeight={500}>{submission.address?.postal_code}</Typography>
            </Grid>
            <Grid item xs={12}>
              <Typography variant="body2" color="text.secondary">Country</Typography>
              <Typography variant="body1" fontWeight={500}>{submission.address?.country}</Typography>
            </Grid>
          </Grid>
        </Box>

        <Divider sx={{ my: 3 }} />

        {/* Documents */}
        <Box sx={{ mb: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
            <DocumentIcon color="primary" />
            <Typography variant="h6" fontWeight={600}>
              Documents ({submission.documents?.length || submission.document_count || 0})
            </Typography>
          </Box>
          {submission.documents && submission.documents.length > 0 ? (
            <Grid container spacing={2}>
              {submission.documents.map((doc, index) => (
                <Grid item xs={12} md={4} key={index}>
                  <Box
                    sx={{
                      p: 2,
                      borderRadius: 2,
                      border: `1px solid ${isDark ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.08)'}`,
                      background: isDark ? 'rgba(255, 255, 255, 0.03)' : 'rgba(0, 0, 0, 0.02)',
                    }}
                  >
                    <Typography variant="subtitle2" fontWeight={600} sx={{ textTransform: 'capitalize', mb: 1 }}>
                      {doc.document_type.replace('_', ' ')}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" noWrap>
                      {doc.file_name}
                    </Typography>
                    <Button
                      size="small"
                      startIcon={<ViewIcon />}
                      onClick={() => handleViewDocument(doc.file_url)}
                      sx={{ mt: 1 }}
                    >
                      View
                    </Button>
                  </Box>
                </Grid>
              ))}
            </Grid>
          ) : (
            <Typography variant="body2" color="text.secondary">
              No documents available
            </Typography>
          )}
        </Box>

        <Divider sx={{ my: 3 }} />

        {/* Submission Info */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="body2" color="text.secondary">
            Submitted: {new Date(submission.submitted_at).toLocaleString()}
          </Typography>
        </Box>

        {/* Review Action */}
        {submission.status === 'pending' || submission.status === 'under_review' ? (
          <Box>
            <Typography variant="h6" fontWeight={600} gutterBottom>
              Review Decision
            </Typography>

            {!action ? (
              <Box sx={{ display: 'flex', gap: 2 }}>
                <Button
                  variant="contained"
                  color="success"
                  startIcon={<ApproveIcon />}
                  onClick={() => setAction('approve')}
                  sx={{
                    background: 'linear-gradient(135deg, #22C55E, #10B981)',
                    '&:hover': {
                      background: 'linear-gradient(135deg, #16A34A, #059669)',
                    },
                  }}
                >
                  Approve
                </Button>
                <Button
                  variant="contained"
                  color="error"
                  startIcon={<RejectIcon />}
                  onClick={() => setAction('reject')}
                  sx={{
                    background: 'linear-gradient(135deg, #EF4444, #DC2626)',
                    '&:hover': {
                      background: 'linear-gradient(135deg, #DC2626, #B91C1C)',
                    },
                  }}
                >
                  Reject
                </Button>
              </Box>
            ) : (
              <Box>
                <Alert
                  severity={action === 'approve' ? 'success' : 'error'}
                  sx={{ mb: 2 }}
                >
                  You are about to <strong>{action}</strong> this KYC submission.
                </Alert>

                {action === 'reject' && (
                  <TextField
                    fullWidth
                    label="Rejection Reason *"
                    placeholder="Please provide a clear reason for rejection"
                    value={reason}
                    onChange={(e) => setReason(e.target.value)}
                    multiline
                    rows={2}
                    required
                    error={action === 'reject' && !reason.trim()}
                    helperText={action === 'reject' && !reason.trim() ? 'Reason is required' : ''}
                    sx={{ mb: 2 }}
                  />
                )}

                <TextField
                  fullWidth
                  label="Notes (Optional)"
                  placeholder="Additional notes for internal reference"
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  multiline
                  rows={2}
                  sx={{ mb: 2 }}
                />

                <Box sx={{ display: 'flex', gap: 2 }}>
                  <Button
                    variant="outlined"
                    onClick={() => {
                      setAction(null);
                      setReason('');
                      setNotes('');
                    }}
                    disabled={loading}
                  >
                    Cancel
                  </Button>
                  <Button
                    variant="contained"
                    color={action === 'approve' ? 'success' : 'error'}
                    onClick={handleSubmitReview}
                    disabled={loading || (action === 'reject' && !reason.trim())}
                    startIcon={loading ? <CircularProgress size={20} color="inherit" /> : null}
                  >
                    {loading ? 'Submitting...' : `Confirm ${action === 'approve' ? 'Approval' : 'Rejection'}`}
                  </Button>
                </Box>
              </Box>
            )}
          </Box>
        ) : (
          <Alert severity="info">
            This submission has already been reviewed ({submission.status}).
          </Alert>
        )}
      </DialogContent>

      <DialogActions sx={{ p: 2 }}>
        <Button onClick={handleClose} disabled={loading}>
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default KYCReviewDialog;
